import type { PublicClient } from "viem";
import { decodeRevert } from "./decodeRevert";
import { pushSimulationError } from "./simulationErrors";

export type WriteCallParams = {
  address: `0x${string}`
  abi: any
  functionName: string
  args: any[]
  account: `0x${string}`
  value?: bigint | number | undefined
  [k: string]: any
}

export type PreviewWriteResult = {
  ok: boolean
  hash?: `0x${string}`
  error?: string
  previewed: boolean
}

/**
 * Optionally PREVIEW a state-changing write with a gas-free static `eth_call`
 * (simulateContract), then — only if the preview succeeds — send the real
 * transaction. If the preview would revert (e.g. a custom MintingDisabled /
 * MintAmountExceedsMaxSupply error), the real tx is NEVER broadcast; the caught
 * error is decoded from the ABI's `error` entries (see decodeRevert) and
 * returned instead.
 *
 * Set `enabled: false` to skip the preview and send directly (legacy behaviour).
 * Set `contractName` (manifest key, e.g. "NDNFTFactoryERC721") so reverts decode
 * to a friendly named error even when the passed ABI lacks the `error` defs.
 *
 * Throws on send failure (non-preview) so callers keep their existing
 * catch/finally semantics.
 */
export async function previewWrite(
  publicClient: PublicClient | undefined,
  walletClient: { writeContract: (p: any) => Promise<`0x${string}`> } | { write: (p: any) => Promise<`0x${string}`> } | undefined,
  params: WriteCallParams,
  opts: { enabled?: boolean; contractName?: string } = {},
): Promise<PreviewWriteResult> {
  const { enabled = true, contractName } = opts
  const writeFn =
    walletClient && typeof (walletClient as any).writeContract === 'function'
      ? (walletClient as any).writeContract.bind(walletClient)
      : walletClient ? (walletClient as any).write.bind(walletClient) : undefined
  if (!publicClient || !writeFn) {
    return { ok: false, error: "No wallet/public client connected", previewed: false }
  }

  if (enabled) {
    // 1) Static preview — eth_call, no gas, no state change. Throws on revert.
    try {
      const { address, abi, functionName, args, account, value, ...rest } = params
      await publicClient.simulateContract!({
        address,
        abi,
        functionName,
        args,
        account,
        value: (value ?? 0n) as any,
        ...rest,
      } as any)
    } catch (e: any) {
      // Preview reverted — block the real tx, surface the decoded error both
      // inline (return value) and in the global simulation-error modal.
      const message = decodeRevert(e, contractName);
      pushSimulationError({
        title: "Simulation reverted — transaction not sent",
        message,
        contract: contractName,
      });
      return { ok: false, error: message, previewed: true };
    }
  }

  // 2) Real send.
  try {
    const hash = await writeFn(params)
    return { ok: true, hash, previewed: enabled }
  } catch (e: any) {
    return { ok: false, error: decodeRevert(e, contractName), previewed: enabled }
  }
}